<template>
  <div class="new-gate-form">
    <v-form ref="formAddGate" v-model="valid" lazy-validation>
      <v-row>
        <v-col cols="12" md="5">
          <div class="new-gate-form__section">
            <h2 class="new-gate-form__section-title">
              <v-icon left small>mdi-information-outline</v-icon>
              Данные о калитке
            </h2>
            <v-row class="new-gate-form__form-row">
              <v-col cols="12" class="new-gate-form__col">
                <v-text-field
                  :value="installationDateTime"
                  label="Дата создания"
                  outlined
                  dense
                  hide-details
                  readonly
                  prepend-inner-icon="mdi-calendar"
                />
              </v-col>
              <v-col cols="12" class="new-gate-form__col">
                <v-combobox
                  v-model="formGate.address"
                  @keyup="startTypingTimer"
                  @input="updateAddressSearch"
                  :rules="[v => !!v || 'Поле обязательно для заполнения']"
                  :items="addresses"
                  item-text="addresses"
                  :search-input.sync="searchAddress"
                  prepend-inner-icon="mdi-rename-box-outline"
                  return-object
                  label="Адрес"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" class="new-gate-form__col">
                <v-autocomplete
                  label="Район (только для г. Саранск)"
                  :items="district"
                  :item-text="item => (item.name) + ' (' + displayCity(item.city_id) + ')'"
                  item-value="id"
                  prepend-inner-icon="mdi-map-marker-radius-outline"
                  v-model="formGate.region"
                  outlined
                  dense
                  hide-details
                  :disabled="!isDistrict"
                  clearable
                />
              </v-col>
              <v-col v-if="!isGateDoorphone" cols="12" class="new-gate-form__col">
                <v-text-field
                  label="Название в моб. приложении"
                  v-model="formGate.name"
                  prepend-inner-icon="mdi-rename-outline"
                  :rules="[() => !!formGate.name || 'Поле обязательно для заполнения']"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>
              <template v-if="isGateDoorphone">
                <v-col cols="12" class="new-gate-form__col">
                  <v-autocomplete
                    label="Управляющая компания"
                    :items="managementCompanies"
                    item-text="name"
                    item-value="id"
                    @change="setGroupManagement"
                    prepend-inner-icon="mdi-home-account"
                    v-model="formGate.managementCompany"
                    outlined
                    dense
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col cols="12" class="new-gate-form__col">
                  <v-text-field
                    label="Компания-собственник"
                    v-model="formGate.groupManagement"
                    prepend-inner-icon="mdi-shield-account"
                    outlined
                    dense
                    hide-details
                    disabled
                  />
                </v-col>
                <v-col cols="12" class="new-gate-form__col">
                  <v-checkbox
                    v-model="formGate.isWithoutPrefix"
                    hide-details
                    dense
                    color="primary"
                    label="Добавить без префикса"
                    :disabled="selectedDoorphones.length >= 1"
                  />
                </v-col>
                <v-col v-if="['admin', 'admin-engineer'].includes(roleName)" cols="12" class="new-gate-form__col">
                  <v-checkbox
                    v-model="formGate.isMassDoorphones"
                    hide-details
                    dense
                    color="primary"
                    label="Добавить несколько домофонов"
                    :disabled="selectedDoorphones.length >= 1"
                  />
                </v-col>
                <v-col v-if="isGateDoorphone && selectedDoorphones.length >= 1" cols="12" class="new-gate-form__col">
                  <p class="new-gate-form__hint text-body-2 mb-0">
                    Удалите все домофоны из списка, чтобы изменить настройки префикса и устройства калитки.
                  </p>
                </v-col>
              </template>
              <v-col cols="12" class="new-gate-form__col">
                <BaseNewBindingDoorphone
                  @addGateDoorphones="handleGateAddDoorphones"
                  @addDoorphones="handleAddDoorphones"
                  @deleteDoorphones="handleDeleteDoorphones"
                  @deleteGateDoorphones="handleGateDeleteDoorphones"
                  :selected-devices="selectedDevices"
                  :is-gate-doorphone="isGateDoorphone"
                  :is-without-prefix="formGate.isWithoutPrefix"
                  :is-multi-doorphones="formGate.isMassDoorphones"
                />
              </v-col>
            </v-row>
          </div>
        </v-col>

        <v-col cols="12" md="7">
          <p v-if="selectedDoorphones.length >= 1" class="new-gate-form__hint text-body-2 mb-2">
            Удалите все домофоны из списка привязанных, чтобы изменить настройки устройства калитки.
          </p>
          <div class="new-gate-form__card" :class="{ 'new-gate-form__card--disabled': selectedDoorphones.length >= 1 }">
            <div class="new-gate-form__filters">
              <div class="new-gate-form__filters-title">
                <v-icon left small>mdi-devices</v-icon>
                Устройства
              </div>
              <div class="new-gate-form__filters-row">
                <v-text-field
                  v-model="searchDevice"
                  label="Поиск по IP-адресу"
                  outlined
                  dense
                  hide-details
                  class="new-gate-form__field"
                  autocomplete="off"
                  @keyup="(e) => e.keyCode === 13 && filterDevices()"
                />
                <v-btn
                  color="primary"
                  class="text-none new-gate-form__search-btn"
                  dark
                  rounded
                  @click.prevent="filterDevices"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Поиск
                </v-btn>
              </div>
            </div>
            <v-data-table
              class="new-gate-form__table"
              v-model="selectedDevices"
              :options.sync="universalOptions"
              :server-items-length="universalTotalDevices"
              :headers="headersDevices"
              :items="devices"
              :aria-disabled="selectedDoorphones.length >= 1"
              item-key="id"
              show-select
              :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
              :single-select="true"
              :disable-sort="true"
            />
          </div>
          <v-alert v-if="errorDevice" type="error" dense outlined class="mt-3">
            Выберите устройство
          </v-alert>
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <div class="new-gate-form__actions">
        <v-btn
          color="primary"
          class="text-none"
          rounded
          @click="validateSelection()"
        >
          <v-icon left>mdi-content-save-outline</v-icon>
          Сохранить
        </v-btn>
        <v-btn
          color="primary"
          outlined
          class="text-none"
          rounded
          @click.prevent="returnGates()"
        >
          <v-icon left>mdi-close</v-icon>
          Отменить
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { headersDevices } from '../../../doorphone/EditDoorphone/consts';
import watchTableOptions from '../../../../mixins/watchTableOptions';
import BaseNewBindingDoorphone from '@/components/BaseNewBindingDoorphone';

export default {
  name: 'BaseNewGate',
  mixins: [watchTableOptions],
  components: { BaseNewBindingDoorphone },
  data () {
    return {
      valid: true,
      searchDevice: '',
      isNewDate: new Date().toISOString().substr(0, 10),
      isFirstLoading: false,
      loading: true,
      searchAddress: '',
      isDistrict: false,
      value: '',
      universalTotalDevices: 0,
      typingTimer: null,
      isGateDoorphone: false,
      universalOptions: {
        itemsPerPage: 10,
        page: 1,
        sortBy: [],
        sortDesc: [],
      },
      headersDevices,
      selectedDevices: [],
      selectedDoorphones: [],
      errorDevice: false,
      isPrefix: false,
      formGate: {
        address: '',
        region: null,
        name: '',
        isWithoutPrefix: false,
        isMassDoorphones: false,
        managementCompany: '',
        groupManagement: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      roleName: 'roleName',
      devices: 'Device/devices',
      addresses: 'Doorphone/addresses',
      district: 'Doorphone/districts',
      managementCompanies: 'ManagementCompany/managementCompanies',
    }),
    installationDateTime () {
      if (this.isNewDate) {
        const date = new Date(this.isNewDate);
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('ru-RU', options);
      } else {
        return '';
      }
    },
  },
  watch: {
    universalOptions (curr, prev) {
      this.handleOptionsChange(curr, prev, () => {
        this.universalChangePage();
      });
    },
    selectedDevices (newValue) {
      this.isGateDoorphone = newValue && newValue.length > 0 && newValue[0]?.model !== 'RACS-1101';
    },
  },
  methods: {
    ...mapActions({
      loadDevices: 'Device/getDevices',
      newGateStore: 'Gate/newGateStore',
      getAddressSearch: 'Doorphone/getAddressSearch',
      loadManagementCompanies: 'ManagementCompany/getManagementCompanies',
      newGateDoorphone: 'Gate/newGate',
    }),
    setGroupManagement (groupCompanyId) {
      const company = this.managementCompanies.find(item => item?.id === groupCompanyId);
      if (company) {
        const management = company.management_group;
        this.formGate.groupManagement = management?.name ?? 'Нет группы';
      } else {
        this.formGate.groupManagement = '';
      }
    },
    validateKeyUp (e) {
      if (e.keyCode === 13) {
        this.filterDoorphones();
      }
    },
    displayCity (item) {
      switch (item) {
        case 1: return 'Ульяновск';
        case 2: return 'Саранск';
        default: return '';
      }
    },
    getAddressString (address) {
      if (address == null) return '';
      if (typeof address === 'object' && address.addresses !== undefined) return address.addresses;
      return typeof address === 'string' ? address : '';
    },
    handleGateAddDoorphones (data) {
      this.selectedDoorphones.push(data);
    },
    handleAddDoorphones (data) {
      this.selectedDoorphones = data;
    },
    handleGateDeleteDoorphones (itemToDelete) {
      this.selectedDoorphones = itemToDelete;
    },
    handleDeleteDoorphones (itemToDelete) {
      this.selectedDoorphones = this.selectedDoorphones.filter(doorphone => doorphone !== itemToDelete.id.toString());
    },
    updateAddressSearch () {
      const val = this.formGate.address;
      if (val && this.isDistrict) {
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(() => {
          if (!this.isFirstLoading && val) {
            this.getAddressSearch({
              value: val,
            })
              .then((res) => {
                this.formGate.region = null;
                this.isDistrict = !!res.districts[0];
              });
          }
        }, 600);
      }
    },
    startTypingTimer (val) {
      clearTimeout(this.typingTimer);
      this.typingTimer = setTimeout(() => {
        if (!this.isFirstLoading && val.srcElement._value) {
          this.getAddressSearch({
            value: val.srcElement._value,
          })
            .then((res) => {
              this.formGate.region = null;
              this.isDistrict = !!res.districts[0];
            });
        }
      }, 600);
    },
    filterDevices () {
      this.universalOptions.page = 1;
      this.universalChangePage();
    },
    universalChangePage () {
      const filters = { free: 1 };

      if (this.searchDevice) {
        filters.ip_address = this.searchDevice;
      }

      this.loadDevices({
        pagination: 1,
        limit: this.universalOptions.itemsPerPage,
        page: this.universalOptions.page,
        filters,
      })
        .then((response) => {
          this.universalTotalDevices = response.meta.total;
        });
    },
    validateGateDoorphone (tempDevice, selectedDoorphonesForBinding) {
      const addressStr = this.getAddressString(this.formGate.address);
      const tempGateDoorphone = {
        device_id: tempDevice,
        address: addressStr,
        without_apartment_prefix: this.formGate.isWithoutPrefix,
      };

      if (this.formGate.region && this.isDistrict) {
        tempGateDoorphone.region_id = this.formGate.region;
      }
      const flatten = (arr) => arr.flatMap(item => Array.isArray(item) ? item : [item]);
      const normalized = flatten(selectedDoorphonesForBinding);
      const doorphoneIds = normalized.map(item => (typeof item === 'object' && item !== null) ? item.id : item);
      const apartmentPrefixes = normalized.map(item => (typeof item === 'object' && item !== null) ? item.apartment_prefix : undefined);
      tempGateDoorphone.doorphones = doorphoneIds;
      if (!this.formGate.isWithoutPrefix && apartmentPrefixes.some(prefix => prefix !== undefined)) {
        tempGateDoorphone.apartment_prefix = apartmentPrefixes;
      }
      if (this.formGate.managementCompany) {
        tempGateDoorphone.management_company_id = this.formGate.managementCompany;
      }

      if (this.formGate.region === null && this.isDistrict) {
        this.$flash_danger('Заполните поле "Район"');
      } else {
        this.newGateDoorphone(tempGateDoorphone)
          .then(() => {
            this.$router.push({ name: 'Gates' });
            this.$flash_success('Калитка-домофон успешно создана');
          })
          .catch((err) => {
            console.error(err);
            if (err.response && err.response.status === 422) {
              const error = err.response.data.device_id;
              error.forEach(item => {
                this.$flash_danger('Ошибка: ' + item);
              });
            } else {
              this.$flash_warning('Ошибка при добавлении калитки-домофон');
            }
          });
      }
    },
    validateGate (tempDevice, selectedDoorphonesForBinding) {
      const addressStr = this.getAddressString(this.formGate.address);
      const tempGate = {
        device_id: tempDevice,
        address: addressStr,
      };
      if (Array.isArray(this.selectedDoorphones[0])) {
        tempGate.doorphones = selectedDoorphonesForBinding
          .flatMap(doorphoneArray => doorphoneArray.map(doorphone => doorphone.id));
      } else {
        tempGate.doorphones = selectedDoorphonesForBinding;
      }
      if (this.formGate.name) {
        tempGate.name = this.formGate.name;
      }
      if (this.formGate.region && this.isDistrict) {
        tempGate.region_id = this.formGate.region;
      }
      if (this.formGate.region === null && this.isDistrict) {
        this.$flash_danger('Заполните поле "Район"');
      } else {
        this.newGateStore(tempGate)
          .then(() => {
            this.$router.push({ name: 'Gates' });
            this.$flash_success('Калитка успешно создана');
          }).catch((error) => {
            this.$sentry_exception(error);
            this.$flash_warning('Ошибка при добавлении калитки');
          });
      }
    },
    validateSelection () {
      this.errorDevice = false;
      let tempDevice;
      let selectedDoorphonesForBinding;
      if (!this.selectedDevices.length) {
        this.$flash_danger('Заполните все поля');
        this.errorDevice = true;
        this.$refs.formAddGate.validate();
        return;
      } else {
        tempDevice = this.selectedDevices[0].id;
      }

      if (this.selectedDoorphones === null || this.selectedDoorphones.length < 1) {
        this.$flash_danger('Привяжите домофон');
        return;
      } else {
        selectedDoorphonesForBinding = this.selectedDoorphones;
      }

      if (this.$refs.formAddGate.validate()) {
        if (this.isGateDoorphone) {
          this.validateGateDoorphone(tempDevice, selectedDoorphonesForBinding);
        } else {
          this.validateGate(tempDevice, selectedDoorphonesForBinding);
        }
      } else {
        this.$flash_danger('Заполните все поля');
      }
    },
    returnGates () {
      this.$router.push({ name: 'Gates' });
    },
  },
  created () {
    const filters = { free: 1 };
    this.loadDevices({
      filters,
      pagination: 1,
      limit: this.universalOptions.itemsPerPage,
    })
      .then((response) => {
        this.universalTotalDevices = response.meta.total;
      })
      .catch((error) => {
        this.$sentry_exception(error);
        this.$flash_warning('Не удалось загрузить список устройств');
      })
      .finally(() => {
        this.loadManagementCompanies();
      });
    setTimeout(() => this.$nextTick(() => {
      this.isFirstLoading = false;
    }));
  },
};
</script>

<style lang="scss" scoped>
.new-gate-form__section {
  margin-bottom: 0;
}

.new-gate-form__section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #37474f;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
}

.new-gate-form__form-row {
  margin: 0 -12px;
}

.new-gate-form__col {
  padding-top: 0;
  padding-bottom: 12px;
}

.new-gate-form__hint {
  color: #546e7a;
}

.new-gate-form__card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  overflow: hidden;
}

.new-gate-form__card--disabled {
  opacity: 0.7;
  pointer-events: none;
}

.new-gate-form__filters {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.new-gate-form__filters-title {
  font-size: 1rem;
  font-weight: 500;
  color: #37474f;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.new-gate-form__filters-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.new-gate-form__field {
  flex: 1;
  min-width: 200px;
}

.new-gate-form__search-btn {
  flex-shrink: 0;
}

.new-gate-form__table {
  border: none;
}

.new-gate-form__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
